import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnInit,
  ViewChild,
} from '@angular/core';
import { IPopulatedTemplate } from '../contextual-actions/interaction-actions/template-context-resolvers/mention-to-templates';
import { TemplateSelectorComponent } from '../template-selector/template-selector.component';
import { BasicMenuButtonComponent } from '@principle-theorem/ng-editor';
import { NgCommonModule } from '@principle-theorem/ng-shared';
import { ITemplatesWithContext } from '@principle-theorem/principle-core/interfaces';
import { snapshot } from '@principle-theorem/shared';
import { getMixedContent } from '@principle-theorem/editor';
import { type MenuButtonLoaderFn } from '@principle-theorem/ng-prosemirror';
import { Observable, Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';

export interface IInsertTemplateMenuData {
  templates$: Observable<ITemplatesWithContext>;
  menuCoordinator$?: Subject<string>;
}

@Component({
  selector: 'pr-insert-template-menu',
  templateUrl: './insert-template-menu.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgCommonModule, TemplateSelectorComponent],
  styleUrls: ['./insert-template-menu.component.scss'],
})
export class InsertTemplateMenuComponent
  extends BasicMenuButtonComponent
  implements OnInit
{
  private static readonly _menuId = 'insert-template';

  override tooltip = 'Insert Template';
  override icon = 'description';
  override buttonText = 'Insert Template';

  @Input() templates$: Observable<ITemplatesWithContext>;
  @Input() menuCoordinator$?: Subject<string>;

  @ViewChild(TemplateSelectorComponent)
  templateSelector: TemplateSelectorComponent;

  ngOnInit(): void {
    this._subscribeToCoordinator();
  }

  async onTemplateSelected(template: IPopulatedTemplate): Promise<void> {
    const editor = await snapshot(this.editor$);
    editor.commands.insertContent(getMixedContent(template.content));
  }

  onMenuOpened(): void {
    this.menuCoordinator$?.next(InsertTemplateMenuComponent._menuId);
  }

  private _subscribeToCoordinator(): void {
    if (!this.menuCoordinator$) {
      return;
    }
    this.menuCoordinator$
      .pipe(
        filter((id) => id !== InsertTemplateMenuComponent._menuId),
        takeUntil(this._onDestroy$)
      )
      .subscribe(() => this.templateSelector?.closeMenu());
  }
}

export const createInsertTemplateMenuButton =
  (data: IInsertTemplateMenuData): MenuButtonLoaderFn =>
  () => ({
    component: InsertTemplateMenuComponent,
    data,
  });
